import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../../../context/ToastContext';
import { API_DOMAIN, headers } from '../../Reusable_Components/Config/apiConfig';

const Profile = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  const [editing, setEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordEmail, setPasswordEmail] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        // Returns User + CustomUser + TenantCredentials fields (see GetUserDetails)
        const response = await axios.get(`${API_DOMAIN}security/audit/get/user/permissions/`, { headers });
        setProfile(response.data);
      } catch (err) {
        console.error('Error fetching profile:', err);
        addToast({ type: 'error', message: 'Failed to load user profile details.' });
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [addToast]);

  const handleEditClick = () => {
    setEditFormData({
      fullName: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username : '',
      email: profile?.email || '',
    });
    setEditing(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (editFormData.email && !emailRegex.test(editFormData.email)) {
      addToast({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    if (profile) {
      // TODO: Wire in backend profile PATCH endpoint here to persist name/email edits
      const [first, ...lastParts] = editFormData.fullName.split(' ');
      setProfile(prev => ({
        ...prev,
        first_name: first || '',
        last_name: lastParts.join(' ') || '',
        email: editFormData.email
      }));
    }

    setEditing(false);
    addToast({ type: 'success', message: 'Profile updated successfully!' });
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!passwordEmail) {
      addToast({ type: 'warning', message: 'Please enter your email address.' });
      return;
    }
    try {
      setPasswordLoading(true);
      await axios.post(`${API_DOMAIN}security/audit/password-reset/`, { email: passwordEmail });
      addToast({ type: 'success', message: `Password reset link sent to ${passwordEmail}!` });
      setShowPasswordModal(false);
      setPasswordEmail('');
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', message: 'Failed to send password reset request. Please check email address.' });
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-body">
          {[70, 50, 85, 60, 75, 45].map((w, i) => (
            <div key={i} className="skeleton" style={{ height: 16, width: `${w}%`, borderRadius: 4, marginBottom: 14 }} />
          ))}
        </div>
      </div>
    );
  }

  const fullName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username : '—';
  const emailVal = profile?.email || '—';
  const roleVal = profile?.role || (profile?.is_superadmin ? 'Super Administrator' : '—');
  const companyVal = profile?.company_name || '—';
  const tenantIdVal = profile?.tenant_id || '—';

  return (
    <div>
      {/* Breadcrumbs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
        <span>Home</span>
        <span>›</span>
        <span style={{ color: 'var(--text-main)', fontWeight: 500 }}>Profile</span>
      </div>

      {/* Page Title & Actions */}
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div className="page-header-left">
          <h1 className="page-title">My Profile</h1>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: 10 }}>
          {editing ? (
            <>
              <button className="btn btn-primary" onClick={handleSave}>Save Changes</button>
              <button className="btn btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={handleEditClick}>Edit Profile</button>
              <button className="btn btn-secondary" onClick={() => setShowPasswordModal(true)}>Change Password</button>
            </>
          )}
        </div>
      </div>

      {/* Profile details card */}
      <div className="profile-card">
        <div className="profile-details-table">
          <div className="profile-details-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="profile-details-label">Full Name</div>
            <div className="profile-details-value">
              {editing ? (
                <input
                  type="text"
                  name="fullName"
                  value={editFormData.fullName}
                  onChange={handleInputChange}
                  className="profile-input"
                />
              ) : (
                fullName
              )}
            </div>
          </div>

          <div className="profile-details-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="profile-details-label">Email</div>
            <div className="profile-details-value">
              {editing ? (
                <input
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleInputChange}
                  className="profile-input"
                />
              ) : (
                emailVal
              )}
            </div>
          </div>

          <div className="profile-details-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="profile-details-label">Username</div>
            <div className="profile-details-value">{profile?.username || '—'}</div>
          </div>

          <div className="profile-details-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="profile-details-label">Role</div>
            <div className="profile-details-value">{roleVal}</div>
          </div>

          <div className="profile-details-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="profile-details-label">Company</div>
            <div className="profile-details-value">{companyVal}</div>
          </div>

          <div className="profile-details-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="profile-details-label">Tenant ID</div>
            <div className="profile-details-value">{tenantIdVal}</div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="profile-modal-overlay">
          <form className="profile-modal" onSubmit={handlePasswordReset}>
            <div className="profile-modal-header">
              <span className="profile-modal-title">Reset Password</span>
              <button
                type="button"
                className="profile-modal-close"
                onClick={() => setShowPasswordModal(false)}
              >
                &times;
              </button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-sub)', lineHeight: 1.4 }}>
              Enter your account email below. We will trigger the secure password reset system and email you a reset link.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                Email Address
              </label>
              <input
                type="email"
                value={passwordEmail}
                onChange={(e) => setPasswordEmail(e.target.value)}
                placeholder="Enter your profile email"
                className="profile-input"
                style={{ textAlign: 'left', width: '100%', padding: '10px' }}
                required
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowPasswordModal(false)}
                disabled={passwordLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={passwordLoading}
              >
                {passwordLoading ? 'Sending...' : 'Send Link'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Profile;
